import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * TradeRegisterDialog — modal for "I took this trade".
 * Props:
 *   open, onOpenChange
 *   defaults: { ticker, direction, entry_price, initial_stop, targets:{t1,t2}, time_exit_ts_ms, source_snapshot_id }
 *   onCreated: (trade) => void
 *
 * NOTE: because this component is mounted once by the parent and toggled via
 * `open`, the initial useState values from `defaults` are locked in at mount
 * time. We therefore sync internal state from `defaults` whenever it changes
 * (and re-seed direction/entry/etc when the dialog is opened) so external
 * pre-fill (e.g. from TradeNowDialog) actually populates the fields.
 */
export default function TradeRegisterDialog({ open, onOpenChange, defaults, onCreated }) {
  const d = defaults || {};
  const [ticker, setTicker] = useState(d.ticker || "");
  const [direction, setDirection] = useState(d.direction || "LONG");
  const [entryPrice, setEntryPrice] = useState(d.entry_price ?? "");
  const [shares, setShares] = useState("");
  const [initialStop, setInitialStop] = useState(d.initial_stop ?? "");
  const [t1, setT1] = useState(d.targets?.t1 ?? "");
  const [t2, setT2] = useState(d.targets?.t2 ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Sync internal state from `defaults` whenever it changes.
  // useState initializers only run on mount, so external prefill would be lost
  // for a persistently-mounted dialog without this effect.
  useEffect(() => {
    if (!defaults) return;
    setTicker(defaults.ticker || "");
    setDirection(defaults.direction || "LONG");
    setEntryPrice(defaults.entry_price ?? "");
    setInitialStop(defaults.initial_stop ?? "");
    setT1(defaults.targets?.t1 ?? "");
    setT2(defaults.targets?.t2 ?? "");
    setShares("");
    setErr("");
  }, [defaults]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = {
        ticker: ticker.trim().toUpperCase(),
        direction,
        entry_price: Number(entryPrice),
        initial_stop: Number(initialStop),
        targets: {
          t1: t1 ? Number(t1) : null,
          t2: t2 ? Number(t2) : null,
        },
        shares: shares ? Number(shares) : null,
        time_exit_ts_ms: d.time_exit_ts_ms ?? null,
        source_snapshot_id: d.source_snapshot_id ?? null,
      };
      const { data } = await api.post("/trades", payload);
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => onOpenChange(false)}>
      <div
        data-testid="register-trade-dialog"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[#0F1626] border border-[#1E293B] mono text-slate-200"
      >
        <div className="flex items-center justify-between border-b border-[#1E293B] px-4 py-3">
          <div className="text-[11px] uppercase tracking-widest text-slate-400">Register Trade — "I took this trade"</div>
          <button onClick={() => onOpenChange(false)} data-testid="register-trade-close" className="text-slate-500 hover:text-slate-200"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          <Row label="Ticker">
            <input data-testid="rt-ticker" required value={ticker} onChange={(e)=>setTicker(e.target.value)} className="input" />
          </Row>
          <Row label="Direction">
            <div className="flex gap-2">
              {["LONG", "SHORT"].map(dv => (
                <button key={dv} type="button" data-testid={`rt-dir-${dv.toLowerCase()}`}
                  onClick={()=>setDirection(dv)}
                  className={`px-3 h-8 border text-[11px] uppercase tracking-widest ${
                    direction === dv
                      ? (dv === "LONG" ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-200" : "border-red-500/60 bg-red-500/15 text-red-200")
                      : "border-[#1E293B] text-slate-500"
                  }`}>{dv}</button>
              ))}
            </div>
          </Row>
          <div className="grid grid-cols-2 gap-2">
            <Row label="Entry Price"><input data-testid="rt-entry" required type="number" step="0.01" value={entryPrice} onChange={(e)=>setEntryPrice(e.target.value)} className="input" /></Row>
            <Row label="Shares (opt)"><input data-testid="rt-shares" type="number" step="1" value={shares} onChange={(e)=>setShares(e.target.value)} className="input" /></Row>
          </div>
          <Row label="Initial Stop">
            <input data-testid="rt-stop" required type="number" step="0.01" value={initialStop} onChange={(e)=>setInitialStop(e.target.value)} className="input" />
          </Row>
          <div className="grid grid-cols-2 gap-2">
            <Row label="T1"><input data-testid="rt-t1" type="number" step="0.01" value={t1} onChange={(e)=>setT1(e.target.value)} className="input" /></Row>
            <Row label="T2"><input data-testid="rt-t2" type="number" step="0.01" value={t2} onChange={(e)=>setT2(e.target.value)} className="input" /></Row>
          </div>
          {err && <div data-testid="rt-error" className="text-[11px] text-red-300">{err}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={()=>onOpenChange(false)} className="px-3 h-8 border border-[#1E293B] text-[11px] uppercase tracking-widest text-slate-400">Cancel</button>
            <button data-testid="rt-submit" disabled={busy} type="submit" className="px-3 h-8 border border-cyan-500/60 bg-cyan-500/15 text-cyan-200 text-[11px] uppercase tracking-widest disabled:opacity-50">
              {busy ? "Registering…" : "Register Trade"}
            </button>
          </div>
        </form>
        <style>{`.input{width:100%;background:#0B111C;border:1px solid #1E293B;color:#E2E8F0;padding:6px 8px;font-family:inherit;font-size:12px;outline:none}.input:focus{border-color:#0891B2}`}</style>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
      {children}
    </label>
  );
}
